import React, { useCallback, useState } from "react";
import { Control, Controller, FieldErrors } from "react-hook-form";
import {
  OnboardBriefcase,
  OnboardInterest,
  OnboardArrow,
  SummaryEmail,
  SummaryFullName,
  SummaryRole,
  SummaryInterest,
  OnboardSummary,
  SummaryBusiness,
} from "@/components/bnp-icons";
import { TextInput } from "@mantine/core";
import { DropdownMenu } from "@/components/bnp-ui";
import PlacesAutocomplete from "./AddressDropDown";
import { getCitiesByState, nigeriaStates } from "@/utils/location";
import OptimizedCaptcha from "@/components/shared/OptimizedCaptcha";

interface StageTwoProps {
  //control: Control<any>;
  control: any;
  errors: FieldErrors;
  setValue: any;
  selectedRole: string;
  selectedUserInterest: string;
  setSelectedUserInterest: (interest: string) => void;
  email: string;
  fullName: string;
  values: any;
  subStep: number;
  setSubStep: (step: number) => void;
}

interface UserInterest {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
}

const StageTwo = ({
  control,
  errors,
  setValue,
  selectedRole,
  selectedUserInterest,
  setSelectedUserInterest,
  email,
  fullName,
  values,
  subStep,
  setSubStep,
}: StageTwoProps) => {
  const [selectedState, setSelectedState] = useState<string>("");
  const [cityOptions, setCityOptions] = useState<string[]>([]);

  const handleCaptchaChange = useCallback(
    (token: string | null) => {
      setValue("recaptchaToken", token || "");
    },
    [setValue],
  );

  React.useEffect(() => {
    if (selectedState) {
      const cities = getCitiesByState(selectedState);
      setCityOptions(cities);
    } else {
      setCityOptions([]);
    }
  }, [selectedState]);

  const interests: UserInterest[] = [
    {
      id: "buy-or-sell",
      title: "I am here to buy/sell properties",
      description: "I want the best deals on properties for sale",
      icon: <OnboardArrow />,
    },
    {
      id: "investment",
      title: "I am here for investments",
      description: "I am looking to invest in properties",
      icon: <OnboardArrow />,
    },
    {
      id: "rent",
      title: "I am here to rent properties",
      description: "Just a quick rent situation",
      icon: <OnboardArrow />,
    },
  ];

  const summaryItems = [
    { title: "EMAIL", value: email, icon: <SummaryEmail /> },
    { title: "FULL NAME", value: fullName, icon: <SummaryFullName /> },
    {
      title: "ROLE",
      value:
        values?.role === "regular-user"
          ? "Buyer"
          : values?.role === "realtor"
            ? "Realtor"
            : values?.role === "developer"
              ? "Developer"
              : "Unknown",
      icon: <SummaryRole />,
    },
    ...(values?.role === "regular-user"
      ? [
          {
            title: "INTEREST",
            value: values?.user_interest
              ? interests.find((i) => i.id === values.user_interest)?.title ||
                values.user_interest
              : "",
            icon: <SummaryInterest />,
          },
        ]
      : [
          {
            title: "BUSINESS NAME",
            value: values?.business_name,
            icon: <SummaryBusiness />,
          },
          {
            title: "BUSINESS ADDRESS",
            value: values?.business_address,
            icon: <SummaryBusiness />,
          },
        ]),
  ];

  // Sub-step 0: Interest selection (regular-user) or Business info (realtor/developer)
  if (subStep === 0 && selectedRole === "regular-user") {
    return (
      <div className="flex flex-col items-center w-[100%] lg:w-[392px]">
        <div className="items-center flex flex-col gap-4 w-[100%] border-b border-soft-200 pb-6 mb-6">
          <OnboardInterest />
          <div className="flex flex-col gap-1 items-center">
            <h1 className="text-main-900 font-semibold text-2xl">More Info</h1>
            <p className="text-base text-grey-7 text-center">
              How will you primarily be using your account?
            </p>
          </div>
        </div>

        <Controller
          name="user_interest"
          control={control}
          render={({ field }) => (
            <div className="flex flex-col gap-3 w-[100%]">
              {interests.map((interest) => (
                <button
                  type="button"
                  key={interest.id}
                  onClick={() => {
                    field.onChange(interest.id);
                    setSelectedUserInterest(interest.id);
                  }}
                  className={`w-full p-4 flex items-center border rounded-lg hover:border-bnp-cyan transition-colors ${
                    field.value === interest.id
                      ? "border-bnp-cyan bg-bnp-teal"
                      : "border-soft-200"
                  }`}
                >
                  <div className="w-10 h-10 flex items-center justify-center">
                    {interest.icon}
                  </div>
                  <div className="ml-4 text-left">
                    <h3 className="font-semibold">{interest.title}</h3>
                    <p className="text-xs text-grey-7">
                      {interest.description}
                    </p>
                  </div>
                  <div className="ml-auto">
                    <div
                      className={`w-6 h-6 rounded-full border-2 ${
                        field.value === interest.id
                          ? "border-bnp-cyan bg-bnp-cyan"
                          : "border-gray-300"
                      }`}
                    >
                      {field.value === interest.id && (
                        <div className="w-full h-full flex items-center justify-center">
                          <div className="w-2 h-2 rounded-full bg-white" />
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        />
      </div>
    );
  }

  if (subStep === 0 && selectedRole !== "regular-user") {
    return (
      <div className="flex flex-col items-center w-[100%] lg:w-[392px]">
        <div className="items-center flex flex-col gap-4 w-[100%] border-b border-soft-200 pb-6 mb-6">
          <OnboardInterest />
          <div className="flex flex-col gap-1 items-center">
            <h1 className="text-main-900 font-semibold text-2xl">
              Business Info
            </h1>
            <p className="text-base text-grey-7 text-center">
              Tell us more about your business
            </p>
          </div>
        </div>

        <div className="w-full flex flex-col gap-6">
          <Controller
            name="business_name"
            control={control}
            rules={{ required: "Business name is required" }}
            render={({ field: { onChange, onBlur, value } }) => (
              <TextInput
                type="text"
                name="business_name"
                label="Business Name"
                value={value}
                withAsterisk
                placeholder="Enter business name"
                leftSection={<SummaryBusiness />}
                size="md"
                radius={"md"}
                className="w-[100%]"
                styles={{
                  input: {
                    minHeight: "44px",
                    fontSize: "16px",
                  },
                }}
                onChange={onChange}
                onBlur={onBlur}
                error={errors.business_name?.message as string | undefined}
              />
            )}
          />

          <PlacesAutocomplete control={control} errors={errors} />

          <div className="items-center flex flex-col lg:flex-row gap-3">
            <Controller
              name="state"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <div className="w-[100%] lg:w-[185px] lg:max-w-[185px] flex flex-col gap-1">
                  <p className="text-sm font-medium text-main-900">State</p>
                  <DropdownMenu
                    name={"state"}
                    value={value}
                    options={nigeriaStates}
                    onChange={(e) => {
                      onChange(e);
                      setSelectedState(e.target.value);
                    }}
                  />
                </div>
              )}
            />

            <Controller
              name="city"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <div className="w-[100%] lg:w-[185px] lg:max-w-[185px] flex flex-col gap-1">
                  <p className="text-sm font-medium text-main-900">City</p>
                  <DropdownMenu
                    name={"city"}
                    value={value}
                    options={cityOptions}
                    onChange={onChange}
                    disabled={!selectedState}
                  />
                </div>
              )}
            />
          </div>
        </div>
      </div>
    );
  }

  // Sub-step 1: Summary + reCAPTCHA
  return (
    <div className="flex flex-col items-center w-[100%] lg:w-[392px]">
      <div className="items-center flex flex-col gap-4 w-[100%] border-b border-soft-200 pb-6 mb-6">
        <OnboardSummary />
        <div className="flex flex-col gap-1 items-center">
          <h1 className="text-main-900 font-semibold text-2xl">
            Complete Setup
          </h1>
          <p className="text-base text-grey-7 text-center">
            Review your information and complete setup
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-4 w-[100%] border border-soft-200 rounded-[20px] p-4 mb-6">
        {summaryItems.map((item: any, index: number) => (
          <div
            key={index}
            className="p-2 flex w-[100%] justify-between items-center border-b border-soft-200 last:border-none"
          >
            <div className="flex items-center gap-3">
              <div>{item?.icon}</div>
              <div className="flex flex-col gap-1">
                <p className="text-grey-7 text-xs font-medium">{item.title}</p>
                <p className="text-sm font-medium text-main-900">
                  {item.value}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
      {/*
  
      <div className="w-full">
        <OptimizedCaptcha
          sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ?? ""}
          onChange={handleCaptchaChange}
        />
        {errors.recaptchaToken && (
          <span className="text-red-500 text-xs mt-2 block">
            {errors.recaptchaToken?.message as string}
          </span>
        )}
      </div>
  
  */}
    </div>
  );
};

export default StageTwo;
